import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BellRing, Eye, RotateCcw, Save, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { StaffSmsPausePanel } from "@/components/StaffSmsPausePanel";

const EVENT = "new_booking";

export default function AdminNewBookingNotifications() {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [log, setLog] = useState([]);
  const [preview, setPreview] = useState(null);
  const [testMobile, setTestMobile] = useState("");
  const [mobileEdit, setMobileEdit] = useState(null); // {id, value}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: c }, { data: u }, { data: l }] = await Promise.all([
      api.get(`/admin/notification-settings/${EVENT}`),
      api.get(`/admin/notification-settings/${EVENT}/eligible-users`),
      api.get(`/admin/notification-settings/${EVENT}/log?limit=15`),
    ]);
    setCfg(c); setUsers(u.users); setLog(l.log);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load settings")); }, []);

  const byRole = useMemo(() => {
    const g = {};
    users.forEach((u) => { (g[u.role] = g[u.role] || []).push(u); });
    return g;
  }, [users]);

  const recipientSummary = useMemo(() => {
    if (!cfg) return [];
    const seen = new Set(), out = [];
    users.forEach((u) => {
      if ((cfg.recipient_roles.includes(u.role) || cfg.recipient_user_ids.includes(u.id))
          && !seen.has(u.email)) { seen.add(u.email); out.push(u); }
    });
    return out;
  }, [cfg, users]);

  const save = async (patch, msg = "Saved") => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/notification-settings/${EVENT}`, patch);
      setCfg((c) => ({ ...c, ...data }));
      toast.success(msg);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleRole = (role) => {
    const roles = cfg.recipient_roles.includes(role)
      ? cfg.recipient_roles.filter((r) => r !== role)
      : [...cfg.recipient_roles, role];
    save({ recipient_roles: roles }, "Recipient roles updated");
  };

  const toggleUser = (u) => {
    const ids = cfg.recipient_user_ids.includes(u.id)
      ? cfg.recipient_user_ids.filter((i) => i !== u.id)
      : [...cfg.recipient_user_ids, u.id];
    save({ recipient_user_ids: ids }, "Recipients updated");
  };

  const saveMobile = async (u) => {
    try {
      const { data } = await api.put(`/admin/internal-users/${u.id}/mobile`, { mobile: mobileEdit.value });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, mobile: data.mobile || "" } : x)));
      setMobileEdit(null);
      toast.success(data.mobile ? `Mobile saved for ${u.name}` : `Mobile removed for ${u.name}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save mobile"); }
  };

  const doPreview = async () => {
    const { data } = await api.post(`/admin/notification-settings/${EVENT}/preview`,
      { subject: cfg.subject, body: cfg.body, sms_body: cfg.sms_body });
    setPreview(data);
  };

  const doTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/notification-settings/${EVENT}/send-test`,
        { subject: cfg.subject, body: cfg.body, sms_body: cfg.sms_body,
          channels: cfg.channels, test_mobile: testMobile });
      if (data.email) data.email.status === "sent"
        ? toast.success(`Test email sent to ${data.email.to} (only you)`)
        : toast.error("Test email failed");
      if (data.sms) data.sms.status === "sent"
        ? toast.success(`Test SMS sent to ${data.sms.to}`)
        : toast.error(data.sms.error || `Test SMS: ${data.sms.status}`);
    } catch { toast.error("Test send failed"); }
    finally { setBusy(false); }
  };

  const doRestore = async () => {
    const { data } = await api.post(`/admin/notification-settings/${EVENT}/restore-default`);
    setCfg((c) => ({ ...c, ...data }));
    setPreview(null);
    toast.success("Default template restored");
  };

  if (!cfg) return <div className="min-h-screen bg-slate-50 p-10 text-sm text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 max-w-4xl mx-auto" data-testid="new-booking-notifications-page">
      <Link to="/admin/settings" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-3.5 h-3.5" /> Settings
      </Link>
      <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <BellRing className="w-6 h-6 text-cyan-600" />
            <h1 className="text-2xl font-bold text-slate-900">New Booking Notifications</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500 max-w-xl">Email selected internal team members whenever a new water-testing booking is created. One notification per booking — refreshes, webhook retries and status updates never re-send.</p>
        </div>
        <label className="flex items-center gap-2.5 rounded-full bg-white border border-slate-200 px-4 py-2">
          <Switch data-testid="nbn-enabled-toggle" checked={cfg.enabled}
            onCheckedChange={(v) => save({ enabled: v }, v ? "Notifications enabled" : "Notifications disabled")} />
          <span className={`text-sm font-bold ${cfg.enabled ? "text-emerald-700" : "text-slate-400"}`}>
            {cfg.enabled ? "Enabled" : "Disabled"}
          </span>
        </label>
      </div>

      {/* Channels */}
      <section className="mt-6 rounded-2xl bg-white border border-slate-200 p-5" data-testid="nbn-channels">
        <h2 className="text-base font-bold text-slate-900">Delivery channel</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {[{ v: ["email"], label: "Email only" }, { v: ["sms"], label: "SMS only" }, { v: ["email", "sms"], label: "Both" }].map((opt) => {
            const active = JSON.stringify([...cfg.channels].sort()) === JSON.stringify([...opt.v].sort());
            return (
              <button key={opt.label} type="button" data-testid={`nbn-channel-${opt.label.toLowerCase().replace(" ", "-")}`}
                onClick={() => save({ channels: opt.v }, `Channel set to ${opt.label}`)}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                  active ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-cyan-400"}`}>
                {opt.label}
              </button>
            );
          })}
        </div>
        {cfg.channels.includes("sms") && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">SMS goes to each recipient's mobile number — recipients without a mobile on their account are skipped for SMS (they still get the email when "Both" is selected).</p>
        )}
      </section>

      {/* Recipients */}
      <section className="mt-5 rounded-2xl bg-white border border-slate-200 p-5" data-testid="nbn-recipients">
        <h2 className="text-base font-bold text-slate-900">Recipients</h2>
        <p className="text-xs text-slate-500 mt-0.5">Tick a role to include everyone in it (now and in future), or select individual people. Duplicates are removed automatically; inactive users never receive notifications.</p>
        <div className="mt-4 space-y-4">
          {cfg.eligible_roles.map((role) => (
            <div key={role.key} className="rounded-xl border border-slate-200 p-3.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid={`nbn-role-${role.key}`}
                  checked={cfg.recipient_roles.includes(role.key)}
                  onChange={() => toggleRole(role.key)}
                  className="w-4 h-4 accent-cyan-600" />
                <span className="text-sm font-bold text-slate-800">All {role.label}s</span>
                <span className="text-xs text-slate-400">({(byRole[role.key] || []).length} active)</span>
              </label>
              {(byRole[role.key] || []).length > 0 && (
                <div className="mt-2 ml-6 space-y-1">
                  {byRole[role.key].map((u) => {
                    const viaRole = cfg.recipient_roles.includes(u.role);
                    const checked = viaRole || cfg.recipient_user_ids.includes(u.id);
                    return (
                      <label key={u.id} className={`flex items-center gap-2 text-sm ${viaRole ? "opacity-60" : "cursor-pointer"}`}>
                        <input type="checkbox" data-testid={`nbn-user-${u.id}`} checked={checked}
                          disabled={viaRole} onChange={() => toggleUser(u)}
                          className="w-3.5 h-3.5 accent-cyan-600" />
                        <span className="font-semibold text-slate-700">{u.name}</span>
                        <span className="text-xs text-slate-400">{u.role_label} · {u.email}</span>
                        {mobileEdit?.id === u.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <input data-testid={`nbn-mobile-input-${u.id}`} type="tel" autoFocus
                              placeholder="+61 4xx xxx xxx" value={mobileEdit.value}
                              onChange={(e) => setMobileEdit({ id: u.id, value: e.target.value })}
                              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), saveMobile(u))}
                              className="rounded-lg border border-slate-300 px-2 py-0.5 text-xs w-36" />
                            <button type="button" data-testid={`nbn-mobile-save-${u.id}`}
                              onClick={() => saveMobile(u)} className="text-[11px] font-bold text-cyan-700">SAVE</button>
                            <button type="button" onClick={() => setMobileEdit(null)} className="text-[11px] text-slate-400">Cancel</button>
                          </span>
                        ) : (
                          <button type="button" data-testid={`nbn-mobile-${u.id}`}
                            onClick={() => setMobileEdit({ id: u.id, value: u.mobile || "" })}
                            className={`text-[11px] font-bold rounded px-1.5 py-0.5 border ${u.mobile
                              ? "text-slate-500 bg-slate-50 border-slate-200 hover:border-cyan-300"
                              : "text-amber-700 bg-amber-50 border-amber-200 hover:border-amber-400"}`}>
                            {u.mobile ? u.mobile : "NO MOBILE — add"}
                          </button>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-slate-600" data-testid="nbn-recipient-summary">
          <b>{recipientSummary.length}</b> recipient{recipientSummary.length === 1 ? "" : "s"} will be notified{recipientSummary.length > 0 && <>: {recipientSummary.map((u) => `${u.name} (${u.email})`).join(", ")}</>}
        </div>
      </section>

      <StaffSmsPausePanel />

      {/* Template */}
      <section className="mt-5 rounded-2xl bg-white border border-slate-200 p-5" data-testid="nbn-template">
        <h2 className="text-base font-bold text-slate-900">Email template</h2>
        <div className="mt-3">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Subject</label>
          <input data-testid="nbn-subject" value={cfg.subject}
            onChange={(e) => setCfg({ ...cfg, subject: e.target.value })}
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" />
        </div>
        <div className="mt-3">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Message body</label>
          <textarea data-testid="nbn-body" rows={12} value={cfg.body}
            onChange={(e) => setCfg({ ...cfg, body: e.target.value })}
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-mono" />
        </div>
        {cfg.channels.includes("sms") && (
          <div className="mt-3">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">SMS message</label>
            <textarea data-testid="nbn-sms-body" rows={3} value={cfg.sms_body}
              onChange={(e) => setCfg({ ...cfg, sms_body: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-mono" />
            <p className="mt-1 text-[11px] text-slate-400">Keep it short — messages over 160 characters are split into multiple SMS segments. Same variables work here.</p>
          </div>
        )}
        <div className="mt-3">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Available variables <span className="font-normal normal-case">(click to copy)</span></div>
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="nbn-variables">
            {cfg.variables.map((v) => (
              <button key={v.key} type="button" title={v.label}
                onClick={() => { navigator.clipboard.writeText(`{{${v.key}}}`); toast.success(`{{${v.key}}} copied`); }}
                className="px-2 py-1 rounded-md bg-slate-100 hover:bg-cyan-50 border border-slate-200 text-[11px] font-mono text-slate-700">
                {"{{"}{v.key}{"}}"}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Lines whose variables are all blank for a booking are left out automatically — no "None" or broken tags.</p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="nbn-save" disabled={busy}
            onClick={() => save({ subject: cfg.subject, body: cfg.body, sms_body: cfg.sms_body }, "Template saved")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold disabled:opacity-50">
            <Save className="w-4 h-4" /> Save
          </button>
          <button data-testid="nbn-preview" onClick={doPreview}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-50">
            <Eye className="w-4 h-4" /> Preview
          </button>
          <button data-testid="nbn-send-test" disabled={busy} onClick={doTest}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-50 disabled:opacity-50">
            <Send className="w-4 h-4" /> Send test (to me only)
          </button>
          <button data-testid="nbn-restore" onClick={doRestore}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-500 text-sm font-bold hover:bg-slate-50">
            <RotateCcw className="w-4 h-4" /> Restore default
          </button>
        </div>
        {cfg.channels.includes("sms") && (
          <div className="mt-3 flex items-center gap-2">
            <input data-testid="nbn-test-mobile" type="tel" placeholder="Your mobile for SMS test (+61…)"
              value={testMobile} onChange={(e) => setTestMobile(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm w-64" />
            <span className="text-[11px] text-slate-400">Used only by "Send test" for the SMS copy.</span>
          </div>
        )}
        {preview && (
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="nbn-preview-panel">
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Preview (sample booking)</div>
            <div className="mt-1.5 text-sm font-bold text-slate-900">{preview.subject}</div>
            <pre className="mt-2 text-sm text-slate-700 whitespace-pre-wrap font-sans">{preview.body}</pre>
            {cfg.channels.includes("sms") && preview.sms_body && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">SMS preview ({preview.sms_body.length} chars)</div>
                <p className="mt-1 text-sm text-slate-700" data-testid="nbn-sms-preview">{preview.sms_body}</p>
              </div>
            )}
          </div>
        )}
        {(cfg.template_updated_by || cfg.updated_by) && (
          <p className="mt-3 text-[11px] text-slate-400" data-testid="nbn-audit">
            {cfg.template_updated_by && <>Template last changed by <b>{cfg.template_updated_by}</b> ({(cfg.template_updated_at || "").slice(0, 16).replace("T", " ")} UTC). </>}
            {cfg.updated_by && <>Settings last changed by <b>{cfg.updated_by}</b> ({(cfg.updated_at || "").slice(0, 16).replace("T", " ")} UTC).</>}
          </p>
        )}
      </section>

      {/* Delivery log */}
      <section className="mt-5 rounded-2xl bg-white border border-slate-200 p-5" data-testid="nbn-log">
        <h2 className="text-base font-bold text-slate-900">Recent notifications</h2>
        {log.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">No notifications sent yet.</p>
        ) : (
          <div className="mt-3 space-y-1.5">
            {log.map((r) => (
              <details key={r.related_id + r.created_at} className="border-b border-slate-100 pb-1.5">
                <summary className="flex items-center justify-between gap-3 text-sm cursor-pointer list-none">
                  <div>
                    <Link to={`/admin/bookings/${r.related_id}`} className="font-bold text-cyan-700 hover:underline">{r.related_id}</Link>
                    <span className="ml-2 text-xs text-slate-400">{(r.created_at || "").slice(0, 16).replace("T", " ")} UTC · {(r.recipients || []).length} deliver{(r.recipients || []).length === 1 ? "y" : "ies"}</span>
                  </div>
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                    r.status === "sent" ? "bg-emerald-100 text-emerald-800"
                    : r.status === "pending" ? "bg-amber-100 text-amber-800"
                    : r.status === "partial" ? "bg-amber-100 text-amber-800"
                    : "bg-red-100 text-red-700"}`}>{r.status.toUpperCase()}</span>
                </summary>
                <div className="mt-1.5 ml-1 space-y-0.5">
                  {(r.recipients || []).map((p, i) => (
                    <div key={i} className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{p.name}</span>
                      <span className="uppercase text-[10px] font-bold text-slate-400">{p.channel || "email"}</span>
                      <span className={`font-bold ${p.status === "sent" ? "text-emerald-700" : p.status.startsWith("skipped") ? "text-amber-700" : "text-red-600"}`}>
                        {p.status.replace(/_/g, " ")}
                      </span>
                      {p.test_mode && <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5" title="TEST_SMS_RECIPIENT is set — SMS was logged but NOT actually delivered. Clear this env var in production.">TEST MODE — NOT DELIVERED</span>}
                      {p.error && <span className="text-red-500">{p.error}</span>}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
